import palette from 'styles/theme/palette';

export default class ReportTemplateStyles {
    static content(theme = {}) {
        return ({
            root: {
                width: '100%',
                maxWidth: '100%',
                flex: '1 1 auto',
                minWidth: 0,
                alignSelf: 'stretch',
                height: '100%',
                overflowX: 'hidden',
                overflowY: 'auto',
                background: palette.grey[200],
                padding: theme.spacing(2.5),
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
            },
            header: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: theme.spacing(2),
                minHeight: 40,
                width: '100%',
            },
            headerTitle: {
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 18,
                fontWeight: 700,
                color: palette.grey[800],
                minWidth: 0,
            },
            headerIcon: {
                color: palette.text.curiousBlue,
                fontSize: 22,
            },
            headerDivider: {
                color: palette.grey[600],
                fontWeight: 400,
            },
            headerReportName: {
                color: palette.grey[600],
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
            newButton: {
                backgroundColor: palette.text.curiousBlue,
                color: palette.text.white,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 6,
                height: 30,
                minHeight: 30,
                padding: '0 12px',
                fontSize: 13,
                lineHeight: 1,
                boxShadow: 'none',
                flexShrink: 0,
                '& .MuiButton-startIcon': {
                    marginRight: 4,
                    '& > *:first-child': {
                        fontSize: 16,
                    },
                },
                '&:hover': {
                    backgroundColor: palette.text.denim,
                    boxShadow: 'none',
                },
            },
            card: {
                background: palette.background.white,
                border: `1px solid ${palette.border.cloud}`,
                borderRadius: 8,
                padding: theme.spacing(2),
                marginBottom: theme.spacing(2),
                boxShadow: '0 1px 2px rgba(33, 43, 54, 0.04)',
                width: '100%',
                boxSizing: 'border-box',
            },
            cardHeader: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: theme.spacing(1.5),
            },
            cardTitleRow: {
                display: 'flex',
                alignItems: 'center',
                gap: 8,
            },
            cardTitle: {
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                color: palette.grey[800],
            },
            cardSubtitle: {
                fontSize: 12,
                color: palette.grey[600],
                marginTop: 2,
            },
            countBadge: {
                minWidth: 22,
                height: 22,
                borderRadius: 11,
                background: palette.background.softGray,
                color: palette.grey[600],
                fontSize: 12,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 7px',
            },
            sectionIcon: {
                fontSize: 16,
                color: palette.grey[600],
            },
            table: {
                '& th': {
                    background: palette.background.porcelain,
                    color: palette.grey[600],
                    fontSize: 12,
                    fontWeight: 600,
                    borderBottom: `1px solid ${palette.border.cloud}`,
                    padding: '10px 12px',
                },
                '& td': {
                    fontSize: 13,
                    color: palette.grey[800],
                    borderBottom: `1px solid ${palette.border.cloud}`,
                    padding: '12px',
                    height: 48,
                    boxSizing: 'border-box',
                },
            },
            reportsTableWrap: {
                width: '100%',
                height: 336, // header (~48) + 6 rows (~48 each)
                maxHeight: 336,
                overflowY: 'auto',
                overflowX: 'auto',
                border: `1px solid ${palette.border.cloud}`,
                borderRadius: 6,
                boxSizing: 'border-box',
                '& thead th': {
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    background: palette.background.porcelain,
                },
            },
            recipientsTableWrap: {
                width: '100%',
                height: 240,
                minHeight: 240,
                maxHeight: 240,
                overflowY: 'auto',
                overflowX: 'auto',
                border: `1px solid ${palette.border.cloud}`,
                borderRadius: 6,
                boxSizing: 'border-box',
            },
            clickableRow: {
                cursor: 'pointer',
                outline: 'none',
                '&:hover': {
                    background: palette.background.aliceBlue,
                },
                '&:focus-visible': {
                    background: palette.background.lavenderBlue,
                    boxShadow: `inset 0 0 0 2px ${palette.text.curiousBlue}`,
                },
                '& td:first-child': {
                    borderLeft: '3px solid transparent',
                    paddingLeft: 9, // 12 - 3 so selection border does not shift content
                },
            },
            selectedRow: {
                background: `${palette.background.lavenderBlue} !important`,
                '& td:first-child': {
                    color: palette.text.curiousBlue,
                    fontWeight: 600,
                    borderLeftColor: palette.text.curiousBlue,
                },
            },
            reportNameCell: {
                display: 'flex',
                alignItems: 'center',
                gap: 8,
            },
            reportDocIcon: {
                fontSize: 16,
                color: palette.grey[600],
            },
            selectedReportDocIcon: {
                color: palette.text.curiousBlue,
            },
            recipientsPill: {
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: palette.background.softGray,
                borderRadius: 12,
                padding: '2px 8px',
                fontSize: 12,
                color: palette.grey[600],
                fontWeight: 600,
            },
            recipientsIcon: {
                fontSize: 14,
            },
            statusDot: {
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: palette.background.emerald,
                display: 'inline-block',
                marginRight: 6,
            },
            muted: {
                color: palette.grey[600],
            },
            nextRunCell: {
                display: 'inline-block',
                minWidth: 120,
                whiteSpace: 'nowrap',
            },
            tableFooter: {
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: theme.spacing(1.5),
                fontSize: 12,
                color: palette.grey[600],
            },
            actionIcon: {
                color: palette.text.slateGray,
                padding: 4,
            },
            actionIconActive: {
                color: palette.text.curiousBlue,
            },
            searchRow: {
                display: 'flex',
                gap: theme.spacing(1),
                marginBottom: theme.spacing(1.5),
                alignItems: 'center',
                width: '100%',
            },
            addButton: {
                backgroundColor: palette.text.curiousBlue,
                color: palette.text.white,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 6,
                height: 30,
                minHeight: 30,
                padding: '0 12px',
                fontSize: 13,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                boxShadow: 'none',
                '& .MuiButton-startIcon': {
                    marginRight: 4,
                    '& > *:first-child': {
                        fontSize: 16,
                    },
                },
                '&:hover': {
                    backgroundColor: palette.text.denim,
                    boxShadow: 'none',
                },
            },
            typeBadge: {
                display: 'inline-flex',
                alignItems: 'center',
                background: palette.background.softGray,
                borderRadius: 10,
                padding: '2px 8px',
                fontSize: 11,
                color: palette.grey[600],
                fontWeight: 600,
            },
            recipientsFooter: {
                marginTop: theme.spacing(1),
                fontSize: 12,
                color: palette.grey[600],
                minHeight: 18,
            },
            scheduleRow: {
                display: 'flex',
                flexWrap: 'wrap',
                gap: theme.spacing(2),
                alignItems: 'flex-end',
                marginTop: 'auto',
                paddingTop: theme.spacing(2),
                borderTop: `1px solid ${palette.border.cloud}`,
                width: '100%',
            },
            scheduleField: {
                minWidth: 150,
            },
            scheduleLabel: {
                display: 'block',
                fontSize: 12,
                fontWeight: 600,
                color: palette.grey[600],
                marginBottom: 6,
            },
            scheduleHint: {
                width: '100%',
                fontSize: 12,
                fontStyle: 'italic',
                color: palette.grey[600],
                marginTop: 4,
                minHeight: 18,
            },
            scheduleActions: {
                marginLeft: 'auto',
                display: 'flex',
                gap: theme.spacing(1),
            },
            runNowButton: {
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 6,
                height: 30,
                minHeight: 30,
                padding: '0 12px',
                fontSize: 13,
                lineHeight: 1,
                borderColor: palette.border.cloud,
                color: palette.grey[800],
                background: palette.background.white,
            },
            previewButton: {
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 6,
                height: 30,
                minHeight: 30,
                padding: '0 12px',
                fontSize: 13,
                lineHeight: 1,
                borderColor: palette.border.cloud,
                color: palette.grey[800],
                background: palette.background.white,
                '&:hover': {
                    background: palette.background.snow,
                    borderColor: palette.border.cloud,
                },
            },
            saveButton: {
                backgroundColor: palette.text.curiousBlue,
                color: palette.text.white,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 6,
                height: 30,
                minHeight: 30,
                padding: '0 12px',
                fontSize: 13,
                lineHeight: 1,
                boxShadow: 'none',
                '&:hover': {
                    backgroundColor: palette.text.denim,
                    boxShadow: 'none',
                },
            },
            emptyState: {
                padding: theme.spacing(5),
                textAlign: 'center',
                color: palette.grey[600],
                fontSize: 14,
            },
            placeholderBox: {
                width: '100%',
                minHeight: 192,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px dashed ${palette.border.cloud}`,
                borderRadius: 6,
                color: palette.grey[600],
                fontSize: 13,
                background: palette.background.snow,
                textAlign: 'center',
                padding: theme.spacing(2),
                boxSizing: 'border-box',
            },
            queryPlaceholder: {
                minHeight: 220,
            },
            templatePlaceholder: {
                minHeight: 140,
            },
            reportsCard: {
                background: palette.background.white,
                border: `1px solid ${palette.border.cloud}`,
                borderRadius: 8,
                padding: theme.spacing(2),
                marginBottom: theme.spacing(2),
                boxShadow: '0 1px 2px rgba(33, 43, 54, 0.04)',
                width: '100%',
                boxSizing: 'border-box',
            },
            detailGrid: {
                width: '100%',
                margin: 0,
                boxSizing: 'border-box',
            },
            detailCard: {
                background: palette.background.white,
                border: `1px solid ${palette.border.cloud}`,
                borderRadius: 8,
                padding: theme.spacing(2),
                marginBottom: theme.spacing(2),
                boxShadow: '0 1px 2px rgba(33, 43, 54, 0.04)',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                boxSizing: 'border-box',
            },
            recipientsCard: {
                minHeight: 520,
            },
            queryCard: {
                minHeight: 300,
            },
            templateCard: {
                minHeight: 220,
            },
            queryWrap: {
                position: 'relative',
                borderRadius: 6,
                overflow: 'hidden',
                border: `1px solid ${palette.background.ebonyClay}`,
                background: palette.background.codGray,
                height: 220,
                minHeight: 220,
                width: '100%',
                boxSizing: 'border-box',
                flexShrink: 0,
            },
            templateEditor: {
                width: '100%',
                height: 140,
                minHeight: 140,
                border: `1px solid ${palette.border.cloud}`,
                borderRadius: 6,
                padding: 12,
                fontSize: 13,
                lineHeight: 1.5,
                resize: 'none',
                color: palette.grey[800],
                background: palette.background.white,
                boxSizing: 'border-box',
                flexShrink: 0,
                '&:focus': {
                    outline: 'none',
                    borderColor: palette.text.curiousBlue,
                },
                '&:disabled': {
                    background: palette.background.snow,
                    color: palette.grey[600],
                },
            },
            tags: {
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                marginTop: theme.spacing(1.5),
                minHeight: 28,
                maxHeight: 64,
                overflowY: 'auto',
            },
            queryLines: {
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                width: 44,
                padding: '12px 0',
                background: palette.background.mineShaft,
                color: palette.text.gray,
                fontSize: 12,
                fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
                textAlign: 'right',
                lineHeight: '18px',
                userSelect: 'none',
                overflow: 'hidden',
                boxSizing: 'border-box',
                zIndex: 2,
                '& span': {
                    display: 'block',
                    height: 18,
                    paddingRight: 8,
                    boxSizing: 'border-box',
                },
            },
            queryHighlight: {
                position: 'absolute',
                top: 0,
                left: 44,
                right: 0,
                bottom: 0,
                padding: '12px 12px',
                margin: 0,
                overflow: 'hidden',
                whiteSpace: 'pre',
                wordBreak: 'normal',
                overflowWrap: 'normal',
                fontSize: 12,
                lineHeight: '18px',
                fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
                color: palette.text.codeGray,
                pointerEvents: 'none',
                boxSizing: 'border-box',
                zIndex: 0,
                '& .sql-keyword': {
                    color: palette.text.orchid,
                    fontWeight: 600,
                },
                '& .sql-string': {
                    color: palette.text.copper,
                },
                '& .sql-number': {
                    color: palette.text.paleOlive,
                },
            },
            queryTextarea: {
                position: 'absolute',
                zIndex: 1,
                top: 0,
                left: 44,
                right: 0,
                bottom: 0,
                width: 'auto',
                height: 'auto',
                minHeight: 0,
                border: 'none',
                outline: 'none',
                resize: 'none',
                background: 'transparent',
                color: 'transparent',
                caretColor: palette.text.white,
                padding: '12px 12px',
                fontSize: 12,
                lineHeight: '18px',
                fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
                whiteSpace: 'pre',
                wordBreak: 'normal',
                overflowWrap: 'normal',
                overflow: 'auto',
                boxSizing: 'border-box',
                '&::placeholder': {
                    color: palette.text.gray,
                    opacity: 1,
                },
            },
            queryStatus: {
                position: 'absolute',
                left: 44,
                right: 0,
                bottom: 0,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '0 12px',
                background: palette.background.mineShaft,
                color: palette.text.statusGray,
                fontSize: 11,
                borderTop: `1px solid ${palette.border.charcoal}`,
            },
            validDot: {
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: palette.background.emerald,
            },
            invalidDot: {
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: palette.red.main,
            },
            timeInput: {
                '& .MuiOutlinedInput-root': {
                    height: 30,
                    fontSize: 13,
                    background: palette.background.white,
                },
            },
        });
    }
}
